import {
  Aperture,
  ArrowSquareOut,
  DownloadSimple,
  Envelope,
  Feather,
  FilePdf,
  Stack,
  SquaresFour,
  Sun,
} from "@phosphor-icons/react";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";

const PDF_URL = "/assets/gledswood/docs/gledswood-styling-packages.pdf";
const PDF_NAME = "Gledswood Styling Packages.pdf";
const COVER_IMAGE = "/assets/gledswood/docs/styling-option-1-whitewood-blooms.jpg";

const ENQUIRY_LINK = `mailto:events@gledswood.com.au?subject=${encodeURIComponent(
  "In-house styling enquiry",
)}&body=${encodeURIComponent(
  "Hi Gledswood Events team,\n\nI'd love to hear about your in-house styling options for our wedding.\n\nKind regards,",
)}`;

const CATEGORIES = [
  { key: "ceremony-arbours", icon: SquaresFour, label: "Ceremony arbours" },
  { key: "floral-accents", icon: Feather, label: "Floral accents & centrepieces" },
  { key: "table-styling", icon: Stack, label: "Table styling" },
  { key: "candles", icon: Sun, label: "Candles, lanterns & fairy lights" },
  { key: "backdrops", icon: Aperture, label: "Backdrops" },
];

const PACKAGES = [
  {
    key: "whitewood-blooms",
    option: "Option 1",
    name: "Whitewood Blooms",
    page: 1,
    includes: [
      "Artificial flower arrangements",
      "Bridal table florals",
      "Bridal table candles",
      "Tealight candles on guest tables",
    ],
    note: "Alternating high and lower centrepieces on guest tables.",
    pricing: [
      ["Bridal table", "$325.00"],
      ["Bridal floor arrangement", "$350.00"],
      ["Guest table", "$95.00 per table"],
      ["Arbour florals", "$375.00"],
    ],
  },
  {
    key: "whispers-of-gold",
    option: "Option 2",
    name: "Whispers of Gold",
    page: 2,
    includes: [
      "A hint of gold and glamour",
      "6 arm gold candelabras",
      "3 taper candle arrangements",
      "Table greenery",
      "Bridal table candles & greenery",
      "Tealight candles on guest tables",
    ],
    note: "Alternating high and lower centrepieces on guest tables.",
    pricing: [
      ["Bridal table", "$225.00"],
      ["Bridal floor arrangement", "$350.00"],
      ["Guest table", "$115.00 per table"],
      ["Arbour florals", "$375.00"],
    ],
  },
  {
    key: "rustic-rose",
    option: "Option 3",
    name: "Rustic Rose",
    page: 3,
    includes: [
      "Artificial rustic tone floral milk pails",
      "Table runner & tea lights",
      "Perspex table number",
      "Bridal table floral garland & tealights",
    ],
    note: "Gledswood reception wedding styling.",
    pricing: [
      ["Bridal table", "$125.00"],
      ["Guest table", "$55.00 per table"],
      ["Arbour florals", "$375.00"],
    ],
  },
];

export default function InHouseStyling() {
  return (
    <div data-testid="styling-page">
      <PageHeader
        eyebrow="In-house styling"
        title="Styled by Gledswood"
        description="Gledswood Styling Packages, current pricing and styling inspiration."
      />
      <section className="grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
        <div>
          <p className="max-w-xl text-sm text-muted-foreground">
            Our in-house team styles your reception end to end — ceremony arbours, florals and
            centrepieces, table styling, candlelight and backdrops. Browse the current packages
            below, then enquire with the Events Team for availability on your date.
          </p>
          <ul className="mt-6 space-y-3" data-testid="styling-categories">
            {CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <li
                  key={category.key}
                  className="flex items-center gap-3 text-sm text-primary"
                  data-testid={`styling-category-${category.key}`}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center border border-border bg-muted/50 rounded-full">
                    <Icon size={17} className="text-secondary" />
                  </span>
                  {category.label}
                </li>
              );
            })}
          </ul>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Button asChild className="rounded-full">
              <a
                href={PDF_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="styling-view-pdf"
              >
                <ArrowSquareOut size={16} className="mr-1" />
                View packages &amp; pricing
              </a>
            </Button>
            <Button asChild variant="outline" className="rounded-full">
              <a href={PDF_URL} download={PDF_NAME} data-testid="styling-download-pdf">
                <DownloadSimple size={16} className="mr-1" />
                Download PDF
              </a>
            </Button>
          </div>
          <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground" data-testid="styling-pricing-note">
            <FilePdf size={14} />
            Current pricing · Gledswood Styling Packages · 3 pages
          </p>
          <a
            className="mt-6 inline-flex items-center gap-2 text-sm text-secondary transition-colors duration-200 hover:text-primary"
            href={ENQUIRY_LINK}
            data-testid="styling-enquiry-link"
          >
            <Envelope size={16} />
            Enquire with the Events Team
          </a>
        </div>
        <a
          href={PDF_URL}
          target="_blank"
          rel="noreferrer"
          className="group relative block overflow-hidden border border-border bg-white rounded-lg"
          data-testid="styling-cover-link"
        >
          <img
            src={COVER_IMAGE}
            alt="Gledswood Styling Packages guide cover"
            className="w-full object-contain"
            data-testid="styling-cover-image"
          />
          <span className="absolute bottom-3 right-3 flex items-center gap-1 bg-primary/85 px-3 py-1 text-[11px] uppercase tracking-wide text-primary-foreground rounded-full transition-colors duration-200 group-hover:bg-primary">
            <ArrowSquareOut size={11} />
            Open full guide
          </span>
        </a>
      </section>

      <section className="mt-12">
        <p className="eyebrow">Packages &amp; pricing</p>
        <h2 className="mt-2 font-heading text-3xl text-primary">Reception styling options</h2>
        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {PACKAGES.map((pack) => (
            <PackageCard key={pack.key} pack={pack} />
          ))}
        </div>
        <p className="mt-5 text-xs text-muted-foreground" data-testid="styling-pricing-disclaimer">
          Pricing is taken from the current Gledswood Styling Packages guide and is confirmed by the
          Events Team with your final styling order.
        </p>
      </section>
    </div>
  );
}

function PackageCard({ pack }) {
  return (
    <article
      className="flex flex-col border border-border bg-card p-6 rounded-lg"
      data-testid={`styling-package-${pack.key}`}
    >
      <p className="eyebrow">{pack.option}</p>
      <h3 className="mt-2 font-heading text-2xl md:text-3xl text-primary">{pack.name}</h3>
      <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
        {pack.includes.map((item) => (
          <li key={item} className="flex gap-2">
            <span className="mt-[7px] h-1 w-1 shrink-0 bg-secondary rounded-full" />
            {item}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-muted-foreground">{pack.note}</p>
      <dl className="mt-5 border-t border-border pt-4 text-sm">
        {pack.pricing.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-3 py-1">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="text-primary">{value}</dd>
          </div>
        ))}
      </dl>
      <a
        href={`${PDF_URL}#page=${pack.page}`}
        target="_blank"
        rel="noreferrer"
        className="mt-5 inline-flex items-center gap-1 text-sm text-secondary transition-colors duration-200 hover:text-primary"
        data-testid={`styling-package-link-${pack.key}`}
      >
        View in guide
        <ArrowSquareOut size={14} />
      </a>
    </article>
  );
}
